<template>
  <section class="tracking-general">
    <div class="tracking-general__hero">
      <div>
        <p class="tracking-general__eyebrow">Seguimiento</p>
        <h1 class="tracking-general__title">Seguimiento General</h1>
        <p class="tracking-general__subtitle">Vista operativa consolidada de tareas y actividad por empresa.</p>
      </div>
    </div>

    <div class="tracking-general__filters">
      <input
        v-model="searchText"
        class="input tracking-general__input"
        type="text"
        placeholder="Buscar cliente por nombre o RUT"
      >
      <div class="select tracking-general__select">
        <select v-model="selectedCompanyStatus">
          <option value="">Todos los estados de cliente</option>
          <option v-for="status in companyStatuses" :key="status" :value="status">{{ status }}</option>
        </select>
      </div>
      <div class="select tracking-general__select">
        <select v-model="selectedTaskStatus">
          <option value="">Tareas abiertas</option>
          <option value="INGRESADA">Ingresada</option>
          <option value="ASIGNADA">Asignada</option>
          <option value="TRABAJANDO">Trabajando</option>
          <option value="TERMINADA">Terminada</option>
        </select>
      </div>
      <div class="select tracking-general__select">
        <select v-model="selectedAssignedUserId">
          <option value="">Todos los responsables</option>
          <option v-for="user in users" :key="user.id" :value="String(user.id)">{{ user.name }}</option>
        </select>
      </div>
      <label class="tracking-general__toggle">
        <input v-model="includeAllCompanies" type="checkbox">
        <span>Incluir empresas sin tareas visibles</span>
      </label>
    </div>

    <div v-if="errorMessage" class="tracking-general__error">{{ errorMessage }}</div>

    <div class="tracking-general__list">
      <article v-for="item in filteredItems" :key="item.company.id" class="tracking-general__card">
        <div class="tracking-general__card-head">
          <div>
            <p class="tracking-general__company">{{ item.company.name }}</p>
            <p class="tracking-general__meta">
              {{ formatRut(item.company) }} · {{ item.company.status }}
            </p>
            <p v-if="item.last_activity" class="tracking-general__meta">
              Última actividad: {{ item.last_activity.title }} · {{ formatDate(item.last_activity.created_at) }}
            </p>
          </div>
          <div class="tracking-general__head-actions">
            <button class="tracking-general__pill" type="button" @click="goToCompany(item.company.id)">
              Abrir cliente
            </button>
            <button class="tracking-general__pill tracking-general__pill--secondary" type="button" @click="toggleExpanded(item.company.id)">
              {{ expandedCompanyId === item.company.id ? 'Ocultar' : 'Ver tareas' }}
            </button>
          </div>
        </div>

        <div class="tracking-general__stats">
          <div class="tracking-general__stat">
            <span>Abiertas</span>
            <strong>{{ item.open_tasks_count }}</strong>
          </div>
          <div class="tracking-general__stat">
            <span>Trabajando</span>
            <strong>{{ item.in_progress_tasks_count }}</strong>
          </div>
          <div class="tracking-general__stat">
            <span>Terminadas</span>
            <strong>{{ item.completed_tasks_count }}</strong>
          </div>
        </div>

        <p class="tracking-general__meta">
          Responsables: {{ formatResponsibles(item.current_responsibles) }}
        </p>

        <div v-if="expandedCompanyId === item.company.id" class="tracking-general__tasks">
          <article v-for="task in item.tasks" :key="task.id" class="tracking-general__task">
            <div>
              <p class="tracking-general__task-title">{{ task.title }}</p>
              <p v-if="task.description" class="tracking-general__task-copy">{{ task.description }}</p>
              <p class="tracking-general__meta">
                {{ task.assigned_user_name || 'Sin asignar' }} · {{ task.status }} · {{ formatDate(task.updated_at || task.created_at) }}
              </p>
            </div>
            <button class="tracking-general__pill tracking-general__pill--secondary" type="button" @click="goToCompany(item.company.id, task.id)">
              Ver detalle
            </button>
          </article>
          <p v-if="!item.tasks.length" class="tracking-general__meta">No hay tareas para esta empresa con los filtros actuales.</p>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import trackingService from '@/services/tracking.service'

export default {
  name: 'TrackingGeneralView',
  data () {
    return {
      items: [],
      users: [],
      companyStatuses: ['Activo', 'Inactivo', 'Esporádico', 'TTG (Trámite de término de Giro)'],
      searchText: '',
      selectedCompanyStatus: '',
      selectedTaskStatus: '',
      selectedAssignedUserId: '',
      includeAllCompanies: false,
      expandedCompanyId: null,
      isLoading: false,
      errorMessage: '',
      debounceTimer: null
    }
  },
  computed: {
    filteredItems () {
      const text = this.searchText.trim().toLowerCase()
      if (!text) return this.items
      return this.items.filter(item => {
        const companyText = `${item.company.name || ''} ${item.company.rut || ''}`.toLowerCase()
        const taskText = (item.tasks || []).map(task => `${task.title || ''} ${task.description || ''}`).join(' ').toLowerCase()
        return companyText.includes(text) || taskText.includes(text)
      })
    }
  },
  methods: {
    formatDate (value) {
      return value ? new Date(value).toLocaleString() : ''
    },
    formatRut (company) {
      return company && company.rut ? `${company.rut}-${company.dv || ''}` : 'Sin RUT'
    },
    formatResponsibles (items) {
      if (!items || !items.length) return 'Sin responsables activos'
      return items.map(item => item.name).join(', ')
    },
    toggleExpanded (companyId) {
      this.expandedCompanyId = this.expandedCompanyId === companyId ? null : companyId
    },
    goToCompany (companyId, taskId = null) {
      const query = { tab: 'tracking' }
      if (taskId) query.task = String(taskId)
      this.$router.push({ name: 'CompanyShow', params: { id: String(companyId) }, query }).catch(() => {})
    },
    scheduleLoad () {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.loadData()
      }, 300)
    },
    async loadUsers () {
      const response = await this.$axios.get('user/getUsers')
      this.users = response.data.users || []
    },
    async loadData () {
      this.isLoading = true
      this.errorMessage = ''
      try {
        this.items = await trackingService.getGeneralTracking({
          status: this.selectedCompanyStatus,
          taskStatus: this.selectedTaskStatus,
          assignedUserId: this.selectedAssignedUserId || undefined,
          includeAllCompanies: this.includeAllCompanies
        })
      } catch (error) {
        this.errorMessage = (error && error.message) || 'No fue posible cargar el seguimiento general.'
      } finally {
        this.isLoading = false
      }
    }
  },
  async mounted () {
    await this.loadUsers()
    await this.loadData()
  },
  beforeDestroy () {
    clearTimeout(this.debounceTimer)
  },
  watch: {
    selectedCompanyStatus () {
      this.scheduleLoad()
    },
    selectedTaskStatus () {
      this.scheduleLoad()
    },
    selectedAssignedUserId () {
      this.scheduleLoad()
    },
    includeAllCompanies () {
      this.scheduleLoad()
    }
  }
}
</script>

<style scoped>
.tracking-general {
  max-width: 1240px;
  margin: 0 auto;
  padding: 1.5rem 1rem 2.5rem;
}

.tracking-general__hero {
  margin-bottom: 1rem;
}

.tracking-general__eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(36, 31, 25, 0.46);
}

.tracking-general__title,
.tracking-general__company,
.tracking-general__task-title {
  margin: 0;
  color: #201811;
}

.tracking-general__subtitle,
.tracking-general__meta,
.tracking-general__task-copy {
  margin: 0.35rem 0 0;
  color: rgba(36, 31, 25, 0.64);
}

.tracking-general__filters {
  display: grid;
  grid-template-columns: 2fr repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.tracking-general__input,
.tracking-general__select select {
  min-height: 2.9rem;
  width: 100%;
  border-radius: 16px;
}

.tracking-general__toggle {
  grid-column: 1 / -1;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(36, 31, 25, 0.72);
}

.tracking-general__error {
  margin-bottom: 1rem;
  color: #b95252;
}

.tracking-general__list {
  display: grid;
  gap: 1rem;
}

.tracking-general__card {
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 26px;
  background: linear-gradient(180deg, rgba(255, 252, 247, 0.94), rgba(246, 239, 231, 0.88));
}

.tracking-general__card-head,
.tracking-general__task {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.tracking-general__head-actions,
.tracking-general__stats {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.tracking-general__stat {
  min-width: 120px;
  padding: 0.8rem 0.9rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.54);
}

.tracking-general__stat span {
  display: block;
  color: rgba(58, 46, 33, 0.48);
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.tracking-general__stat strong {
  color: #241c16;
  font-size: 1.15rem;
}

.tracking-general__tasks {
  display: grid;
  gap: 0.7rem;
  margin-top: 0.9rem;
  padding-top: 0.9rem;
  border-top: 1px solid rgba(95, 76, 52, 0.08);
}

.tracking-general__task {
  padding: 0.9rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.62);
}

.tracking-general__pill {
  min-height: 2.3rem;
  padding: 0 0.85rem;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #241c15 0%, #4b3a29 100%);
  color: #f7f0e5;
}

.tracking-general__pill--secondary {
  background: rgba(255, 255, 255, 0.82);
  color: #352b21;
  border: 1px solid rgba(104, 83, 57, 0.14);
}

@media (max-width: 980px) {
  .tracking-general__filters {
    grid-template-columns: 1fr;
  }

  .tracking-general__card-head,
  .tracking-general__task {
    flex-direction: column;
  }
}
</style>
